import React, { useState, useEffect, useRef } from 'react';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  const searchInputRef = useRef(null);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = '';
      clearSearch();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim().length >= 2) {
      setShowSuggestions(false);
      // Simulate search results
      setTimeout(() => {
        const mockResults = [
          {
            type: 'task',
            title: `Task containing "${query}"`,
            description: 'Complete the user interface design for the dashboard',
            status: 'active',
            priority: 'high',
            project: 'UI/UX Project'
          },
          {
            type: 'project', 
            title: `Project: ${query} Management`,
            description: 'Project management system development',
            status: 'pending',
            members: 5
          },
          {
            type: 'file',
            title: `${query}.pdf`,
            description: 'Design specifications document',
            size: '2.4 MB'
          }
        ];
        setSearchResults(mockResults);
      }, 300);
    } else {
      setShowSuggestions(true);
      setSearchResults([]);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(true);
    setSearchResults([]);
    searchInputRef.current?.focus();
  };

  const getResultIcon = (type) => {
    const icons = {
      task: 'fas fa-tasks',
      project: 'fas fa-folder',
      file: 'fas fa-file-alt',
      team: 'fas fa-users',
      message: 'fas fa-comment'
    };
    return icons[type] || 'fas fa-file';
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    // Simulate search
    handleSearchInput({ target: { value: suggestion } });
  };

  const handleResultClick = (result) => {
    console.log('Navigate to:', result.title);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`search-modal ${isOpen ? 'show' : ''}`}>
      <div className="search-modal-overlay" onClick={onClose}></div>
      <div className="search-modal-content">
        <div className="search-modal-header">
          <div className="search-input-container">
            <i className="fas fa-search search-icon"></i>
            <input 
              ref={searchInputRef}
              type="text" 
              className="search-input" 
              placeholder="Search projects, tasks, files, and more..." 
              value={searchQuery}
              onChange={handleSearchInput}
              autoComplete="off"
            />
            {searchQuery && (
              <button className="search-clear" onClick={clearSearch} aria-label="Clear search">
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <button className="search-close" onClick={onClose} aria-label="Close search">
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="search-modal-body">
          {showSuggestions ? (
            <div className="search-suggestions">
              <div className="suggestions-section">
                <div className="suggestions-title">Recent Searches</div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('E-commerce redesign')}>
                  <i className="fas fa-clock"></i>
                  <span>E-commerce redesign</span>
                </div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('Mobile app wireframes')}>
                  <i className="fas fa-clock"></i>
                  <span>Mobile app wireframes</span>
                </div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('Budget allocation document')}>
                  <i className="fas fa-clock"></i>
                  <span>Budget allocation document</span>
                </div>
              </div>

              <div className="suggestions-section">
                <div className="suggestions-title">Quick Actions</div>
                <div className="suggestion-item action-item" onClick={() => console.log('Create new project')}>
                  <i className="fas fa-plus"></i>
                  <span>Create new project</span>
                </div>
                <div className="suggestion-item action-item" onClick={() => console.log('Add new task')}>
                  <i className="fas fa-tasks"></i>
                  <span>Add new task</span>
                </div>
                <div className="suggestion-item action-item" onClick={() => console.log('Invite team member')}>
                  <i className="fas fa-users"></i>
                  <span>Invite team member</span>
                </div>
              </div>

              <div className="suggestions-section">
                <div className="suggestions-title">Popular</div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('Marketing Campaign')}>
                  <i className="fas fa-project-diagram"></i>
                  <span>Marketing Campaign</span>
                  <span className="suggestion-type">Project</span>
                </div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('Design Homepage Layout')}>
                  <i className="fas fa-tasks"></i>
                  <span>Design Homepage Layout</span>
                  <span className="suggestion-type">Task</span>
                </div>
                <div className="suggestion-item" onClick={() => handleSuggestionClick('Requirements Document v2.pdf')}>
                  <i className="fas fa-file-alt"></i>
                  <span>Requirements Document v2.pdf</span>
                  <span className="suggestion-type">File</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="search-results">
              <div className="search-filters">
                <button className="filter-tab active" data-filter="all">All</button>
                <button className="filter-tab" data-filter="projects">Projects</button>
                <button className="filter-tab" data-filter="tasks">Tasks</button>
                <button className="filter-tab" data-filter="files">Files</button>
                <button className="filter-tab" data-filter="people">People</button>
              </div>

              <div className="results-container">
                {searchResults.map((result, index) => (
                  <div key={index} className="results-section">
                    <div className="result-item" onClick={() => handleResultClick(result)}>
                      <div className="result-icon">
                        <i className={getResultIcon(result.type)}></i>
                      </div>
                      <div className="result-content">
                        <div className="result-title">{result.title}</div>
                        <div className="result-description">{result.description}</div>
                        <div className="result-meta">
                          {result.status && <span className={`result-status ${result.status}`}>{result.status}</span>}
                          {result.priority && <span className={`result-priority ${result.priority}`}>{result.priority}</span>}
                          {result.project && <span className="result-project">{result.project}</span>}
                          {result.size && <span className="result-size">{result.size}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
